import { html, LitElement, nothing, PropertyValues } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { until } from 'lit/directives/until.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import type { TemplateResult } from 'lit';
import type {
  DataTableFilterDescriptor,
  DataTableDataProvider,
} from './data-table-provider';
import {
  DataTableController,
  DataTableControllerState,
  DataTableControllerCriteria,
} from './data-table-controller';

import './data-table-paginator';
import { DataTablePaginator } from './data-table-paginator';
import { dataTableStyles } from './data-table.styles';

export function awaitableTemplate(
  template:
    | TemplateResult
    | string
    | Promise<TemplateResult>
    | Promise<string>
    | HTMLElement
    | Promise<HTMLElement>
    | undefined
    | null
    | (() => string | TemplateResult)
    | (() => TemplateResult[])
    | (() => Promise<string | TemplateResult>)
    | (() => Promise<TemplateResult[]>)
) {
  if (!template) return html``;
  const templateValue = typeof template === 'function' ? template() : template;

  return (templateValue as Partial<Promise<unknown>>).then
    ? until(templateValue)
    : templateValue;
}

/**
 * Returns True if handled. if false, the event will go to the cellclick
 */
export type DataTableCellClickEvent = (
  e: Event,
  table: DataTable,
  item: unknown,
  fieldName: string
) => boolean;

/**
 * A single-argument callback over a table row of consumer-defined shape.
 *
 * The table is intentionally row-type-agnostic, so its row parameter is
 * `unknown`. Extracting the type from a method member (rather than declaring a
 * plain `(item: unknown) => R` property) keeps the parameter *bivariant* - the
 * documented "bivariance hack" React's own typings use - so a consumer may
 * assign a precisely row-typed callback (e.g. `(item: IProduct) => item.id`)
 * without either side reaching for `any`. A plain function-property type would
 * reject that assignment under `strictFunctionTypes`.
 */
type RowCallback<R> = { fn(item: unknown): R }['fn'];

// Id is a unique value to query on if set.
export interface DataTableColumnDef {
  id?: string;
  title: string | TemplateResult | (() => string | TemplateResult);
  fieldName: string;
  classes?: string;
  width?: string; // CSS width value e.g. '150px', '10rem', 'auto'
  minWidth?: string; // CSS min-width value
  maxWidth?: string; // CSS max-width value
  fixed?: 'left' | 'right';
  sortable?: boolean;
  isDefaultSortDirectionAscending?: boolean;
  // Method-signature syntax (not an arrow property) is deliberate: it makes the
  // `item` parameter bivariant, so a consumer can supply a row-typed callback
  // (e.g. `(t, item: ICategory) => ...`) against this row-type-agnostic table
  // without an `any`. Property syntax would reject that under strictFunctionTypes.
  displayValue?(
    table: DataTable,
    item: unknown,
    index: number,
    fieldName: string
  ): TemplateResult | string;
  click?: DataTableCellClickEvent;
  visible?: boolean;
  columnAggregate?: () => TemplateResult | string;
  align?: 'left' | 'center' | 'right';
  /**
   * When set (boolean true or a per-item predicate), the cell renders an
   * editor (see {@link DataTableColumnDef.editor}) when the table is in
   * edit mode. Read-only columns omit this field.
   */
  editable?: boolean | RowCallback<boolean>;
  /**
   * Returns the editor template for this cell. Called whenever the cell
   * should render as an editor (rules depend on `editMode`). The consumer
   * is responsible for the editor's value bind + change handler — the
   * table only controls focus and commit timing.
   */
  editor?(table: DataTable, item: unknown, fieldName: string): TemplateResult;
}

/**
 * The mode the table operates in for inline editing.
 *
 *  - `none`  — read-only (default).
 *  - `cell`  — spreadsheet-style: only the focused cell renders as an editor.
 *              Click an editable cell to activate it.
 *  - `row`   — the selected row's editable columns render as editors.
 *  - `table` — every editable column in every visible row renders as an
 *              editor (stock-take style).
 */
export type DataTableEditMode = 'none' | 'cell' | 'row' | 'table';

/**
 * Detail attached to the `cell-commit` and `row-commit` events.
 *
 * Consumers can:
 *   - call `waitFor(promise)` to defer navigation until validation /
 *     persistence settles. Multiple waitFor calls are awaited concurrently.
 *   - call `preventNav()` to cancel the pending nav (focus stays put).
 *
 * If any registered promise rejects, navigation is also cancelled. This
 * gives the consumer the "no race / strict order" contract: the next
 * cell-commit will not fire until the current one's persistence is done.
 */
export interface DataTableCellCommitDetail {
  table: DataTable;
  item: object;
  rowIndex: number;
  fieldName: string;
  waitFor: (promise: Promise<unknown>) => void;
  preventNav: () => void;
}

export interface DataTableRowCommitDetail {
  table: DataTable;
  item: object;
  rowIndex: number;
  waitFor: (promise: Promise<unknown>) => void;
  preventNav: () => void;
}

export interface DataTableRowAddDetail {
  table: DataTable;
  newItem: object;
  insertIndex: number;
}

export type DataTableRowExtensionTemplateEvent =
  | ((item: unknown) => TemplateResult)
  | ((item: unknown) => Promise<TemplateResult>)
  | ((item: unknown) => DataTableExtensionResult)
  | ((item: unknown) => Promise<DataTableExtensionResult>);

export type DataTableRowExtensionTemplate =
  | TemplateResult
  | Promise<TemplateResult>
  | DataTableExtensionResult
  | Promise<DataTableExtensionResult>;

export type DataTableExtensionTemplateProvider =
  DataTableRowExtensionTemplate | DataTableRowExtensionTemplateEvent;

/** Compound result for extension templates */
export interface DataTableExtensionResult {
  template: TemplateResult;
}

interface DataTableExtension {
  itemKey: unknown;
  extensionProvider: DataTableExtensionTemplateProvider;
}

/** Template function for group header rows */
export type DataTableGroupHeaderTemplate = (
  groupValue: unknown,
  groupItems: object[],
  table: DataTable
) => TemplateResult;

export interface TableItemMove {
  sourceIndex: number;
  targetIndex: number;
}

export interface DataTableFetchOptions {
  sliceStart?: number;
  requiresCount?: boolean;
  append?: boolean;
}

/**
 * @event fetchtemplate - Emitted requesting row template.
 * @event {{ table: DataTable, item: Object }} rowclick - Emitted when a row is clicked.
 * @event {{ table: DataTable, selectedkeys: Object }} rowselect - Emitted when a row is selected.
 * @event {{ table: DataTable, item: Object, colDef: DataTableColumnDef, originalEvent: Event }} cellclick - Emitted when a cell is clicked.
 * @event {{ Object : TableItemMove }} -table-item-move - Emitted when items are moved (drag/drop).
 * @event {DataTableCellCommitDetail} cell-commit - Emitted before focus moves out of an editor cell. Consumer can validate via waitFor / preventNav.
 * @event {DataTableRowCommitDetail} row-commit - Emitted before focus moves out of an editor row.
 * @event {DataTableRowAddDetail} row-add - Emitted when add-row zone is activated (click or tab past last). Consumer pushes detail.newItem to the source array.
 *
 * @tag app-data-table
 */
export class DataTable extends LitElement {
  static styles = [dataTableStyles];

  private dragSrcEl: HTMLDivElement | null = null;
  private lastDropTarget: HTMLElement | null = null;
  private draggedIndex: number | null = null;
  private noDropClass = 'no-drop-allowed';
  private controller = new DataTableController<object>();
  private controllerUnsubscribe?: () => void;

  @property() rowClass = 'data-table-row';
  @property() colClass = 'data-table-cell';

  @property({ type: String })
  enableDrag?: string;
  @property({ attribute: false })
  dataProvider?: DataTableDataProvider<object>;
  @property({ attribute: false })
  dataFilters: DataTableFilterDescriptor[] = [];
  @property({ type: String })
  freeTextFilter?: string;
  @property({ type: Boolean })
  autoFetchOnLoad = true;

  @property({ type: String })
  bodyMaxHeight?: string;

  @property({ type: String })
  minTableWidth?: string;

  /**
   * When true, columns without explicit width will be auto-sized based on
   * header and content. Set to false to use equal flex distribution.
   */
  @property({ type: Boolean })
  autoSizeColumns = true;

  @property({ type: Boolean })
  clickrows = false;
  @property({ type: String })
  selectmode = 'none'; // none | single | multi
  @property({ type: String })
  tablestyle = 'standardtable'; //standard, nested, "", small
  @property({ type: Boolean })
  requiresCount = false;
  @property({ type: Number })
  rowCount = 0;
  @property({ type: Boolean })
  showLoadMore = false;
  @property({ attribute: false })
  keyevent?: RowCallback<unknown>;

  @property({ attribute: false })
  rowidevent?: RowCallback<string | null>;

  @property({ attribute: false })
  itemRowClassEvent?: RowCallback<string>;
  @property({ attribute: false })
  checkEnabledDragEvent?: RowCallback<boolean>;

  /** Field name to group rows by. Data should be sorted by this field. */
  @property({ type: String })
  groupBy?: string;

  /**
   * Function to extract a group key from a row item.
   * When provided, this takes precedence over the `groupBy` field name.
   * The returned string is used as the group key and for ordering groups.
   */
  @property({ attribute: false })
  getGroupByKey?: (item: object) => string | null;

  /** Custom template for group header rows. If not provided, uses default. */
  @property({ attribute: false })
  groupHeaderTemplate?: DataTableGroupHeaderTemplate;

  /**
   * Inline-edit mode. See {@link DataTableEditMode}.
   *
   * Editor templates and editable predicates live on the column defs
   * (`editor`, `editable`).
   */
  @property({ type: String })
  editMode: DataTableEditMode = 'none';

  /**
   * When set, the table renders an "Add row" zone at the bottom and lets
   * the user tab / down-arrow past the last row to add a new one. The
   * factory must return a fresh row object; the table emits a `row-add`
   * event with that object — the consumer is responsible for pushing it
   * into the source array.
   */
  @property({ attribute: false })
  addRowFactory?: () => object;

  private _columns: DataTableColumnDef[] = [
    {
      title: 'Column1',
      fieldName: '',
      classes: 'col-6',
      displayValue: () => 'One',
    },
    {
      title: 'Column2',
      fieldName: '',
      classes: 'col-6',
      displayValue: () => 'Two',
    },
  ];

  @property({ type: Array })
  set columns(value: DataTableColumnDef[]) {
    const nextColumns = value ?? [];
    this.validateFixedColumns(nextColumns);
    const oldValue = this._columns;
    this._columns = nextColumns;
    this.fixedColumnLayoutDirty = true;
    this.requestUpdate('columns', oldValue);
  }

  get columns() {
    return this.enableDrag === 'true'
      ? [
          {
            title: '',
            classes: 'colpxmax-30 drag-grab-handle',
            fieldName: 'xx',
            displayValue: (
              _table: DataTable,
              _item: unknown,
              _index: number
            ) => {
              return html`<span
                @touchstart="${this.dragtouchstart}"
                @touchmove="${this.dragtouchmove}"
                @touchend="${this.dragtouchend}"
                @touchcancel="${this.handleTouchCancel}"
                class="draggable-item"
                ><i class="fa-solid fa-grip-vertical"></i
              ></span>`;
            },
          },
          ...this._columns,
        ]
      : this._columns;
  }

  private validateFixedColumns(columns: DataTableColumnDef[]) {
    type Phase = 'left' | 'middle' | 'right';
    let phase: Phase = 'left';
    columns.forEach((column) => {
      const fixed = column.fixed;
      if (fixed === 'left') {
        if (phase !== 'left') {
          throw new Error(
            'Fixed left columns must be defined contiguously at the start of the column set.'
          );
        }
        return;
      }

      if (fixed === 'right') {
        phase = 'right';
        return;
      }

      if (phase === 'right') {
        throw new Error(
          'Non-fixed columns cannot appear after fixed right columns. Move fixed right columns to the end.'
        );
      }

      phase = 'middle';
    });
  }

  @state()
  data: object[] = [];
  @state()
  private controllerState: DataTableControllerState<object> | null = null;
  @state()
  private isFullscreen = false;
  @state()
  private _computedWidths: Map<string, number> = new Map();
  private _autoSizeScheduled = false;
  private hasRequestedData = false;
  private suppressNextProviderReload = false;
  @property({ type: Number })
  pageLength = 3000;
  @state()
  private _extensions: DataTableExtension[] = [];

  /** Active editor cell for `editMode === 'cell'`. Tracked by primary key. */
  @state()
  private _editingCellKey: unknown = null;
  @state()
  private _editingCellField: string | null = null;

  /**
   * Set during nav when we want focus to land on a specific cell after the
   * next render (e.g. after add-row pushes a new item to the source array).
   * Cleared once applied.
   */
  private _pendingFocus: { rowIndex: number; fieldName: string } | null = null;

  /** Re-entrancy guard for nav: prevents overlapping commit chains. */
  private _navInFlight = false;

  /** Bound focusout handler so we can detach in disconnectedCallback. */
  private _focusOutHandler?: (e: FocusEvent) => void;

  /** Tracks the row currently holding the keyboard focus, for click-out commit. */
  private _focusedRowIndex: number | null = null;

  private _firstConnected = false;
  private fixedColumnLayoutDirty = true;
  private fixedColumnOffsetFrame: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private bodyScrollElement: HTMLElement | null = null;
  private headerContainerElement: HTMLElement | null = null;
  private bodyScrollListener?: (event: Event) => void;

  @property({ type: String })
  sortField?: string;
  @property({ type: Boolean })
  sortAscending?: boolean;
  @property({ type: Array })
  selectedKeys: unknown[] = [];

  @property({ type: Number })
  private _pageIndex = 0;

  constructor() {
    super();
    this.controllerUnsubscribe = this.controller.subscribe((state) => {
      this.controllerState = state;
      this.data = [...(state.items as object[])];
      this.rowCount =
        state.totalCount !== undefined ? state.totalCount : this.data.length;
    });
  }

  public get pageIndex(): number {
    return this._pageIndex;
  }

  public set pageIndex(value: number) {
    this._pageIndex = this.pageNumberCheck(value);
    if (!this.dataProvider) return;
    if (!this.autoFetchOnLoad && !this.hasRequestedData) return;
    void this.requestDataFromProvider({
      reset: true,
      sliceStart: this._pageIndex * this.pageLength,
    });
  }

  private get shouldUseLoadMore(): boolean {
    return !!this.dataProvider && this.showLoadMore;
  }

  protected get standardtable(): boolean {
    return this.tablestyle === 'standardtable' || this.tablestyle === '';
  }

  protected get nestedtable(): boolean {
    return this.tablestyle === 'nestedtable';
  }

  protected get halftable(): boolean {
    return this.tablestyle === 'halftable';
  }

  protected get isSortingAscending(): boolean {
    return this.sortAscending === undefined || this.sortAscending;
  }

  protected get pageCount(): number {
    return Math.ceil(this.rowCount / this.pageLength);
  }

  protected shouldRenderPaginator(): boolean {
    if (this.shouldUseLoadMore) return false;
    return this.pageCount > 1;
  }

  protected get isSelectable(): boolean {
    return this.selectmode !== 'none';
  }

  connectedCallback(): void {
    if (!this._firstConnected) this.configure();
    this._firstConnected = true;
    this.observeSizeChanges();
    if (!this._focusOutHandler) {
      this._focusOutHandler = (e: FocusEvent) => this.handleTableFocusOut(e);
      this.addEventListener('focusout', this._focusOutHandler);
    }
    super.connectedCallback();
  }

  disconnectedCallback(): void {
    this.controllerUnsubscribe?.();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.fixedColumnOffsetFrame !== null) {
      cancelAnimationFrame(this.fixedColumnOffsetFrame);
      this.fixedColumnOffsetFrame = null;
    }
    if (this._focusOutHandler) {
      this.removeEventListener('focusout', this._focusOutHandler);
      this._focusOutHandler = undefined;
    }
    this.teardownScrollContainers();
    super.disconnectedCallback();
  }

  /**
   * When focus leaves the table entirely (and not into another cell still
   * inside it), commit the row that held focus. Defers one microtask so
   * `document.activeElement` reflects the new owner, then walks across
   * any shadow boundaries to determine "still inside this table?".
   */
  private handleTableFocusOut(_e: FocusEvent) {
    if (this.editMode === 'none') return;
    if (this._focusedRowIndex === null) return;
    void Promise.resolve().then(() => {
      if (this.isFocusInsideTable()) return;
      const rowIndex = this._focusedRowIndex;
      if (rowIndex === null) return;
      this._focusedRowIndex = null;
      const item = this.data[rowIndex] as object | undefined;
      if (!item) return;
      void this.commitRowInternal(item, rowIndex);
    });
  }

  private isFocusInsideTable(): boolean {
    let active: Element | null = document.activeElement;
    while (active) {
      if (active === this) return true;
      const root = active.getRootNode();
      if (root instanceof ShadowRoot) {
        active = root.host;
      } else {
        active = active.parentElement;
      }
    }
    return false;
  }

  protected updated(changedProps: PropertyValues): void {
    super.updated(changedProps);

    let shouldReload = false;
    const canAutoLoad = this.autoFetchOnLoad || this.hasRequestedData;

    if (changedProps.has('dataProvider')) {
      if (this.dataProvider) {
        this.controller.setSliceLength(this.pageLength);
        this.controller.setProvider(this.dataProvider);
        if (this.suppressNextProviderReload)
          this.suppressNextProviderReload = false;
        else if (canAutoLoad) shouldReload = true;
      } else {
        this.hasRequestedData = false;
      }
    }

    if (
      changedProps.has('dataFilters') ||
      changedProps.has('freeTextFilter') ||
      changedProps.has('requiresCount')
    ) {
      if (this.hasRequestedData) shouldReload = true;
    }

    if (changedProps.has('pageLength')) {
      this.controller.setSliceLength(this.pageLength);
      if (this.dataProvider && this.hasRequestedData) shouldReload = true;
    }

    if (shouldReload && this.dataProvider && canAutoLoad) {
      void this.requestDataFromProvider({ reset: true });
    }

    if (
      this.fixedColumnLayoutDirty ||
      changedProps.has('columns') ||
      changedProps.has('data') ||
      changedProps.has('enableDrag') ||
      changedProps.has('sortField') ||
      changedProps.has('sortAscending')
    ) {
      this.queueFixedColumnOffsets();
      this.fixedColumnLayoutDirty = false;
    }

    // Schedule auto-sizing when data or columns change
    if (changedProps.has('data') || changedProps.has('columns')) {
      this.scheduleAutoSize();
    }

    // Re-trigger auto-sizing when autoSizeColumns is toggled on
    if (changedProps.has('autoSizeColumns')) {
      if (this.autoSizeColumns) {
        // Clear old widths and re-measure
        this._computedWidths = new Map();
        this.scheduleAutoSize();
      } else {
        // Clear computed widths when auto-sizing is disabled
        this._computedWidths = new Map();
      }
    }

    // Apply queued focus (e.g. after add-row data arrived, or after a
    // cell-mode click activated an editor that wasn't yet rendered).
    //
    // In `cell` mode the destination cell only renders an editor when
    // _editingCellKey/Field match. If they don't yet (typical after
    // add-row, where the new item only appears once data prop updates),
    // bind them now and re-queue focus for the next render.
    if (this._pendingFocus) {
      const { rowIndex, fieldName } = this._pendingFocus;
      this._pendingFocus = null;
      if (this.editMode === 'cell') {
        const targetItem = this.data[rowIndex] as object | undefined;
        if (targetItem) {
          const targetKey = this.getPrimaryKey(targetItem);
          if (
            this._editingCellKey !== targetKey ||
            this._editingCellField !== fieldName
          ) {
            this._editingCellKey = targetKey;
            this._editingCellField = fieldName;
            this._pendingFocus = { rowIndex, fieldName };
            return;
          }
        } else {
          // Target row not present yet; consumer hasn't pushed it. Re-queue
          // and wait for the next data update.
          this._pendingFocus = { rowIndex, fieldName };
          return;
        }
      }
      requestAnimationFrame(() => this.focusCell(rowIndex, fieldName));
    }

    this.setupScrollContainers();
  }

  configure() {
    //override;
  }

  private composeCriteria(): DataTableControllerCriteria {
    const sort: DataTableControllerCriteria['sort'] = this.sortField
      ? [
          {
            field: this.sortField,
            direction: this.isSortingAscending ? 'asc' : 'desc',
          },
        ]
      : [];
    return {
      sort: sort,
      filters: this.dataFilters,
      freeText: this.freeTextFilter,
      requiresCount: this.requiresCount,
    };
  }

  private async requestDataFromProvider(options?: {
    reset?: boolean;
    sliceStart?: number;
    requiresCount?: boolean;
  }) {
    if (!this.dataProvider) return;
    this.controller.setProvider(this.dataProvider);

    if (options?.reset === false) {
      await this.controller.loadMore({
        sliceStart: options.sliceStart,
        sliceLength: this.pageLength,
        requiresCount: options.requiresCount,
      });
    } else {
      await this.controller.loadInitial(this.composeCriteria(), {
        sliceStart: options?.sliceStart ?? 0,
        sliceLength: this.pageLength,
        requiresCount: options?.requiresCount ?? this.requiresCount,
      });
    }

    this.hasRequestedData = true;
  }

  public async fetchData(options?: DataTableFetchOptions) {
    await this.requestDataFromProvider({
      reset: options?.append !== true,
      sliceStart: options?.sliceStart,
      requiresCount: options?.requiresCount,
    });
  }

  isExpanded(item: object) {
    const itemKey = this.getPrimaryKey(item);
    return this._extensions.find((x) => x.itemKey === itemKey);
  }

  addExtension(item: object, template: DataTableExtensionTemplateProvider) {
    const itemKey = this.getPrimaryKey(item);
    // Save scroll position before state change triggers re-render
    const scrollContainer = this.bodyScrollElement;
    const scrollTop = scrollContainer?.scrollTop ?? 0;
    const scrollLeft = scrollContainer?.scrollLeft ?? 0;

    this._extensions = [
      ...this._extensions.filter((x) => x.itemKey !== itemKey),
      { itemKey: itemKey, extensionProvider: template },
    ];

    // Restore scroll position after render
    void this.updateComplete.then(() => {
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollTop;
        scrollContainer.scrollLeft = scrollLeft;
      }
    });
  }

  remExtension(item: object) {
    const itemKey = this.getPrimaryKey(item);
    // Save scroll position before state change triggers re-render
    const scrollContainer = this.bodyScrollElement;
    const scrollTop = scrollContainer?.scrollTop ?? 0;
    const scrollLeft = scrollContainer?.scrollLeft ?? 0;

    this._extensions = this._extensions.filter((x) => x.itemKey !== itemKey);

    // Restore scroll position after render
    void this.updateComplete.then(() => {
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollTop;
        scrollContainer.scrollLeft = scrollLeft;
      }
    });
  }

  fetchTemplateEvent(item: object) {
    this.dispatchCustom('fetchtemplate', { table: this, item: item });
  }

  afterRowTemplate(item: object, index: number) {
    const event = {
      table: this,
      item: item,
      index: index,
      template: undefined,
    };
    this.dispatchCustom('after-row-render', event);
    return event.template;
  }

  beforeRowTemplate(item: object, index: number) {
    const event = {
      table: this,
      item: item,
      index: index,
      template: undefined,
    };
    this.dispatchCustom('before-row-render', event);
    return event.template;
  }

  rowClickEvent(item: object) {
    this.dispatchCustom('rowclick', { table: this, item: item });
  }

  rowSelectEvent() {
    this.dispatchCustom('rowselect', {
      table: this,
      selectedkeys: this.selectedKeys,
    });
  }

  cellClickEvent(
    originalEvent: Event,
    item: object,
    colDef: DataTableColumnDef
  ) {
    this.dispatchCustom('cellclick', {
      table: this,
      item: item,
      colDef: colDef,
      originalEvent: originalEvent,
    });
  }

  getPrimaryKey(item: object): unknown {
    if (this.keyevent) return this.keyevent(item);
    return item;
  }

  getItemRowClass(item: object): string {
    return this.itemRowClassEvent?.(item) ?? '';
  }

  getRowId(item: object): string | null {
    if (this.rowidevent) return this.rowidevent(item);

    return null;
  }

  enableDragOnRow(item: object) {
    if (this.enableDrag !== 'true') return false;

    if (this.checkEnabledDragEvent) return this.checkEnabledDragEvent(item);

    //Default is to return true
    return true;
  }

  /**
   * override this and validate the page is allowed against a max page etc
   * @param value
   * @returns
   */
  protected pageNumberCheck(value: number): number {
    return value;
  }

  protected firstUpdated() {
    if (!this.dataProvider || !this.autoFetchOnLoad || this.hasRequestedData)
      return;
    this.controller.setSliceLength(this.pageLength);
    this.suppressNextProviderReload = true;
    void this.requestDataFromProvider({ reset: true, sliceStart: 0 });
  }

  protected render(): TemplateResult {
    const paginator = this.shouldRenderPaginator()
      ? html`<div class="data-table-paginator-row">
          <app-data-table-paginator
            @page-change=${(e: CustomEvent) =>
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-member-access
              (this.pageIndex = e.detail.index)}
            index=${this.pageIndex}
            count=${this.pageCount}
          ></app-data-table-paginator>
          <sl-icon-button
            class="data-table-fullscreen-toggle"
            name=${this.isFullscreen ? 'fullscreen-exit' : 'arrows-fullscreen'}
            label=${
              this.isFullscreen ? 'Exit fullscreen' : 'Expand to fullscreen'
            }
            @click=${this.toggleFullscreen}
          ></sl-icon-button>
        </div>`
      : html``;
    const bodyMaxHeightStyle = this.bodyMaxHeight
      ? styleMap({ maxHeight: this.bodyMaxHeight })
      : styleMap({});
    const minTableWidthStyle = this.minTableWidth
      ? styleMap({ minWidth: this.minTableWidth })
      : styleMap({});

    return html`
      <div
        class="data-table data-table ${this.tablestyle}"
        style=${minTableWidthStyle}
      >
        <div class="data-table-scroll" style=${bodyMaxHeightStyle}>
          <div class="data-table-header data-table-header--sticky">
            <div class="${this.rowClass} data-table-header-row">
              ${this.headerTemplate()}
            </div>
          </div>
          <div class="data-table-content">
            ${until(this.bodyTemplate(), html``)} ${this.addRowZoneTemplate()}
          </div>
        </div>
        ${this.loadMoreTemplate()}
        <div class="data-table-footer">${this.footerTemplate()}</div>
        ${paginator}
      </div>
    `;
  }

  /**
   * Big clickable strip below the last row when {@link addRowFactory} is
   * set. Mouse click + Enter/Space activate it; tab/down-arrow past the
   * last row also routes here (via {@link triggerAddRow}).
   */
  protected addRowZoneTemplate(): TemplateResult | typeof nothing {
    if (!this.addRowFactory) return nothing;
    const onActivate = () => this.triggerAddRow(this.data.length);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    };
    return html`<div
      class="data-table-add-row"
      role="button"
      tabindex="0"
      @click=${onActivate}
      @keydown=${onKey}
    >
      <span class="data-table-add-row-icon" aria-hidden="true">+</span>
      <span class="data-table-add-row-label">Add row</span>
    </div>`;
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  protected async bodyTemplate(): Promise<TemplateResult | TemplateResult[]> {
    const data = this.getPageData();

    // If no groupBy or getGroupByKey, render normally
    if (!this.groupBy && !this.getGroupByKey) {
      return html`${repeat(
        data,
        (item) => this.getPrimaryKey(item),
        (item, index) => this.rowTemplate(item, index)
      )}`;
    }

    // Group rows by the groupBy field or getGroupByKey function
    const groups = this.buildGroups(data);

    // Render with group headers
    const templates: TemplateResult[] = [];
    let rowIndex = 0;

    for (const group of groups) {
      // Add group header
      templates.push(this.renderGroupHeader(group.value, group.items));

      // Add rows in this group
      for (const item of group.items) {
        templates.push(this.rowTemplate(item, rowIndex));
        rowIndex++;
      }
    }

    return html`${templates}`;
  }

  private buildGroups(
    data: object[]
  ): Array<{ value: unknown; items: object[] }> {
    const groups: Array<{ value: unknown; items: object[] }> = [];
    let currentGroup: { value: unknown; items: object[] } | null = null;

    for (const item of data) {
      // Use getGroupByKey function if provided, otherwise fall back to field lookup
      const value = this.getGroupByKey
        ? this.getGroupByKey(item)
        : this.getFieldValue(item, this.groupBy ?? '');

      if (currentGroup === null || currentGroup.value !== value) {
        currentGroup = { value, items: [] };
        groups.push(currentGroup);
      }

      currentGroup.items.push(item);
    }

    return groups;
  }

  private renderGroupHeader(
    groupValue: unknown,
    groupItems: object[]
  ): TemplateResult {
    // Custom template provided - wrap it in the standard group header structure
    if (this.groupHeaderTemplate) {
      const customContent = this.groupHeaderTemplate(
        groupValue,
        groupItems,
        this
      );
      return html`
        <div
          class="${this.rowClass} data-table-row data-table-group-header-row"
        >
          <div class="data-table-group-header-cell">${customContent}</div>
        </div>
      `;
    }

    // Default template with simple label and count
    const displayValue =
      groupValue !== null && groupValue !== undefined
        ? String(groupValue)
        : '(No value)';

    return html`
      <div class="${this.rowClass} data-table-row data-table-group-header-row">
        <div class="data-table-group-header-cell">
          <span class="data-table-group-header-label">${displayValue}</span>
          <span class="data-table-group-header-count"
            >(${groupItems.length})</span
          >
        </div>
      </div>
    `;
  }

  private getFieldValue(item: object, fieldName: string): unknown {
    // Handle nested field names like "user.name"
    const parts = fieldName.split('.');
    let value: unknown = item;
    for (const part of parts) {
      if (value === null || value === undefined) return undefined;
      value = (value as Record<string, unknown>)[part];
    }
    return value;
  }

  private loadMoreTemplate(): TemplateResult | typeof nothing {
    if (!this.shouldUseLoadMore) return nothing;
    const loading = this.controllerState?.loading ?? false;
    const hasMore = this.controllerState?.hasMore ?? false;
    if (!hasMore && !loading) return nothing;

    const loadedCount = this.data.length;
    const totalCount = this.controllerState?.totalCount;
    const showCount = hasMore && totalCount !== undefined && totalCount > 0;

    return html`
      <div class="data-table-load-more">
        ${
          showCount
            ? html`<span class="load-more-count"
                >${loadedCount} of ${totalCount} loaded</span
              >`
            : nothing
        }
        <button
          class="load-more-button"
          @click=${this.handleLoadMoreClick}
          ?disabled=${loading || !hasMore}
        >
          ${loading ? 'Loading…' : 'Load more'}
        </button>
        <sl-icon-button
          class="data-table-fullscreen-toggle"
          name=${this.isFullscreen ? 'fullscreen-exit' : 'arrows-fullscreen'}
          label=${
            this.isFullscreen ? 'Exit fullscreen' : 'Expand to fullscreen'
          }
          @click=${this.toggleFullscreen}
        ></sl-icon-button>
      </div>
    `;
  }

  private toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    this.classList.toggle('data-table-fullscreen', this.isFullscreen);
    document.body.classList.toggle(
      'data-table-fullscreen-active',
      this.isFullscreen
    );
  }

  private async handleLoadMoreClick() {
    if (!this.dataProvider || this.controllerState?.loading) return;

    // Save internal scroll position before loading
    const scrollContainer = this.bodyScrollElement;
    const scrollTop = scrollContainer?.scrollTop ?? 0;
    const scrollLeft = scrollContainer?.scrollLeft ?? 0;

    await this.fetchData({ append: true });

    // Restore internal scroll position after render
    if (scrollContainer) {
      requestAnimationFrame(() => {
        scrollContainer.scrollTop = scrollTop;
        scrollContainer.scrollLeft = scrollLeft;
      });
    }
  }

  protected columnTemplate(
    item: object,
    itemIndex: number,
    colDef: DataTableColumnDef,
    colindex: number,
    columnCount: number
  ): TemplateResult {
    const renderEditor = this.shouldRenderEditor(colDef, item);

    const baseHandler = colDef.click
      ? (e: Event) => {
          if (!colDef.click?.(e, this, item, colDef.fieldName))
            this.cellClickEvent(e, item, colDef);
          else e.stopImmediatePropagation();
        }
      : (e: Event) => {
          //dont stop propagation. row click may want this
          this.cellClickEvent(e, item, colDef);
        };

    // In `cell` edit mode, clicking an editable cell that isn't yet active
    // activates it and queues focus. In `table` mode editors are always
    // active so the activation step is unnecessary.
    const cellModeActivate =
      this.editMode === 'cell' &&
      !renderEditor &&
      this.isCellEditable(colDef, item);

    const clickEvent = cellModeActivate
      ? (e: Event) => {
          // If a different cell was active, commit it before switching.
          // Click is synchronous; we fire-and-forget — the consumer's
          // waitFor still completes the persistence pipeline, but we
          // don't block the click on it.
          if (
            this._editingCellKey !== null &&
            this._editingCellField !== null &&
            (this._editingCellKey !== this.getPrimaryKey(item) ||
              this._editingCellField !== colDef.fieldName)
          ) {
            const prevKey = this._editingCellKey;
            const prevField = this._editingCellField;
            const prevItem = this.data.find(
              (r) => this.getPrimaryKey(r as object) === prevKey
            );
            if (prevItem) {
              const prevIndex = this.data.indexOf(prevItem);
              void this.commitCellInternal(
                prevItem as object,
                prevIndex,
                prevField
              );
            }
          }
          this._editingCellKey = this.getPrimaryKey(item);
          this._editingCellField = colDef.fieldName;
          this._pendingFocus = {
            rowIndex: itemIndex,
            fieldName: colDef.fieldName,
          };
          baseHandler(e);
        }
      : baseHandler;

    const cellContent = renderEditor
      ? (colDef.editor?.(this, item, colDef.fieldName) ?? html``)
      : (colDef.displayValue?.(this, item, itemIndex, colDef.fieldName) ??
        (item as Record<string, unknown>)[colDef.fieldName] ??
        '');

    const posClass =
      colindex === 0 ? 'first' : colindex === columnCount - 1 ? 'last' : '';
    const fixedClass = colDef.fixed
      ? ` data-table-cell--fixed data-table-cell--fixed-${colDef.fixed}`
      : '';
    const alignClass = colDef.align ? `text-${colDef.align}` : '';
    const editorClass = renderEditor ? 'data-table-cell--editor' : '';
    const editableClass =
      this.isCellEditable(colDef, item) && this.editMode !== 'none'
        ? 'data-table-cell--editable'
        : '';
    const colClasses =
      `${colDef.classes ?? ''} ${posClass} ${fixedClass} ${alignClass} ${editorClass} ${editableClass}`.trim();
    const fixedAttr = colDef.fixed ?? nothing;
    const cellStyle = this.getCellWidthStyle(colDef);

    return colDef.click || cellModeActivate
      ? html` <div
          role="button"
          @click=${clickEvent}
          class="${this.colClass} ${colClasses}"
          style=${cellStyle}
          data-col-index=${colindex}
          data-field=${colDef.fieldName}
          data-fixed=${fixedAttr}
        >
          ${cellContent}
        </div>`
      : html` <div
          @click=${clickEvent}
          class="${this.colClass} ${colClasses}"
          style=${cellStyle}
          data-col-index=${colindex}
          data-field=${colDef.fieldName}
          data-fixed=${fixedAttr}
        >
          ${cellContent}
        </div>`;
  }

  /** True when the column declares itself editable for this item. */
  private isCellEditable(colDef: DataTableColumnDef, item: object): boolean {
    if (!colDef.editor) return false;
    if (colDef.editable === undefined) return false;
    if (typeof colDef.editable === 'function') {
      try {
        return !!colDef.editable(item);
      } catch {
        return false;
      }
    }
    return colDef.editable === true;
  }

  /** True when the cell should render its editor template right now. */
  private shouldRenderEditor(
    colDef: DataTableColumnDef,
    item: object
  ): boolean {
    if (this.editMode === 'none') return false;
    if (!this.isCellEditable(colDef, item)) return false;
    if (this.editMode === 'table') return true;
    if (this.editMode === 'row') {
      return this.selectedKeys.indexOf(this.getPrimaryKey(item)) > -1;
    }
    // 'cell'
    return (
      this._editingCellKey === this.getPrimaryKey(item) &&
      this._editingCellField === colDef.fieldName
    );
  }

  /**
   * Editable columns for this row in display order. Used for tab nav.
   */
  private getEditableFields(item: object): DataTableColumnDef[] {
    return this.columns.filter(
      (c) => this.isVisible(c) && this.isCellEditable(c, item)
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // Edit-mode keyboard nav + commit pipeline
  // ────────────────────────────────────────────────────────────────────────

  /**
   * Row-level keydown handler. Fires for keys originating inside any cell
   * editor in this row (events bubble up through composed shadow DOM). We
   * intercept Tab/Enter/Arrow/Esc and route them through `navigateOut`,
   * which awaits the consumer's cell-commit / row-commit promises before
   * moving focus.
   */
  private handleEditKeyDown(
    event: KeyboardEvent,
    item: object,
    rowIndex: number
  ) {
    if (this.editMode === 'none') return;
    const target = event.target as HTMLElement | null;
    if (!target) return;

    // Find the field this event came from. Editors live inside a cell with
    // a `data-field` attribute. If we can't find one, the event isn't from
    // an editor — let it through.
    const cellEl = target.closest<HTMLElement>('[data-field]');
    if (!cellEl) return;
    const fieldName = cellEl.dataset.field;
    if (!fieldName) return;

    const colDef = this.columns.find((c) => c.fieldName === fieldName);
    if (!colDef || !this.isCellEditable(colDef, item)) return;

    switch (event.key) {
      case 'Tab': {
        event.preventDefault();
        event.stopPropagation();
        void this.navigateOut(
          item,
          rowIndex,
          fieldName,
          event.shiftKey ? -1 : 1,
          'horizontal'
        );
        return;
      }
      case 'Enter': {
        // Let textareas use Enter for newlines. `target.tagName` is the
        // host tag when Shoelace retargets across shadow DOM, hence both
        // checks.
        if (target.tagName === 'TEXTAREA' || target.tagName === 'SL-TEXTAREA')
          return;
        event.preventDefault();
        event.stopPropagation();
        void this.navigateOut(item, rowIndex, fieldName, 1, 'vertical');
        return;
      }
      case 'ArrowUp': {
        event.preventDefault();
        event.stopPropagation();
        void this.navigateOut(item, rowIndex, fieldName, -1, 'vertical');
        return;
      }
      case 'ArrowDown': {
        event.preventDefault();
        event.stopPropagation();
        void this.navigateOut(item, rowIndex, fieldName, 1, 'vertical');
        return;
      }
      case 'Escape': {
        event.stopPropagation();
        if (this.editMode === 'cell') {
          this._editingCellKey = null;
          this._editingCellField = null;
        }
        return;
      }
      default:
        return;
    }
  }

  /**
   * Awaitable cell-commit. Dispatches `cell-commit` with `waitFor` +
   * `preventNav` extras and resolves to true if nav should proceed.
   */
  private async commitCellInternal(
    item: object,
    rowIndex: number,
    fieldName: string
  ): Promise<boolean> {
    let prevented = false;
    const promises: Promise<unknown>[] = [];
    const detail: DataTableCellCommitDetail = {
      table: this,
      item,
      rowIndex,
      fieldName,
      waitFor: (p) => {
        promises.push(p);
      },
      preventNav: () => {
        prevented = true;
      },
    };
    this.dispatchCustom('cell-commit', detail);
    if (promises.length) {
      try {
        await Promise.all(promises);
      } catch {
        return false;
      }
    }
    return !prevented;
  }

  /** Awaitable row-commit. See {@link commitCellInternal}. */
  private async commitRowInternal(
    item: object,
    rowIndex: number
  ): Promise<boolean> {
    let prevented = false;
    const promises: Promise<unknown>[] = [];
    const detail: DataTableRowCommitDetail = {
      table: this,
      item,
      rowIndex,
      waitFor: (p) => {
        promises.push(p);
      },
      preventNav: () => {
        prevented = true;
      },
    };
    this.dispatchCustom('row-commit', detail);
    if (promises.length) {
      try {
        await Promise.all(promises);
      } catch {
        return false;
      }
    }
    return !prevented;
  }

  /**
   * Compute the cell to focus next, run cell- and row-commits along the
   * way, and either focus the target cell or trigger add-row.
   *
   *   horizontal +1: next editable cell, or first editable of next row,
   *                  or add-row if past last.
   *   horizontal -1: previous editable cell, or last editable of prev row.
   *   vertical   ±1: same field, prev/next row (or add-row past last).
   */
  private async navigateOut(
    item: object,
    rowIndex: number,
    fieldName: string,
    direction: 1 | -1,
    axis: 'horizontal' | 'vertical'
  ) {
    if (this._navInFlight) return;
    this._navInFlight = true;
    try {
      let targetRowIndex = rowIndex;
      let targetField = fieldName;
      let triggerAddRow = false;

      if (axis === 'horizontal') {
        const fields = this.getEditableFields(item);
        const idx = fields.findIndex((c) => c.fieldName === fieldName);
        const nextIdx = idx + direction;
        if (nextIdx >= 0 && nextIdx < fields.length) {
          targetField = fields[nextIdx].fieldName;
        } else if (direction === 1) {
          const nextItem = this.data[rowIndex + 1] as object | undefined;
          if (nextItem) {
            const nextFields = this.getEditableFields(nextItem);
            if (nextFields.length) {
              targetRowIndex = rowIndex + 1;
              targetField = nextFields[0].fieldName;
            } else {
              return;
            }
          } else if (this.addRowFactory) {
            triggerAddRow = true;
            targetRowIndex = rowIndex + 1;
          } else {
            return;
          }
        } else {
          const prevItem = this.data[rowIndex - 1] as object | undefined;
          if (!prevItem) return;
          const prevFields = this.getEditableFields(prevItem);
          if (!prevFields.length) return;
          targetRowIndex = rowIndex - 1;
          targetField = prevFields[prevFields.length - 1].fieldName;
        }
      } else {
        // vertical
        const next = rowIndex + direction;
        if (next < 0) return;
        if (next >= this.data.length) {
          if (direction === 1 && this.addRowFactory) {
            triggerAddRow = true;
            targetRowIndex = next;
          } else {
            return;
          }
        } else {
          targetRowIndex = next;
        }
      }

      // Always commit the cell we're leaving.
      if (!(await this.commitCellInternal(item, rowIndex, fieldName))) return;
      // Commit the row only if we're crossing rows.
      if (targetRowIndex !== rowIndex) {
        if (!(await this.commitRowInternal(item, rowIndex))) return;
      }

      if (triggerAddRow) {
        // In cell mode, point the editor target at the new row's first
        // editable field so it renders as an editor when the consumer
        // pushes the new item back.
        if (this.editMode === 'cell' && this._pendingFocus) {
          this._editingCellKey = null; // resolved in updated() once data arrives
          this._editingCellField = this._pendingFocus.fieldName;
        }
        this.triggerAddRow(targetRowIndex);
      } else {
        // In cell mode the destination cell also has to render its editor.
        if (this.editMode === 'cell') {
          const targetItem = this.data[targetRowIndex] as object | undefined;
          if (targetItem) {
            this._editingCellKey = this.getPrimaryKey(targetItem);
            this._editingCellField = targetField;
            // editor renders next frame; focusCell needs to wait for it
            this._pendingFocus = {
              rowIndex: targetRowIndex,
              fieldName: targetField,
            };
          }
        } else {
          this.focusCell(targetRowIndex, targetField);
        }
      }
    } finally {
      this._navInFlight = false;
    }
  }

  /**
   * Move keyboard focus to the editor inside the (rowIndex, fieldName) cell.
   * No-ops if the cell or its focusable child can't be located.
   */
  private focusCell(rowIndex: number, fieldName: string) {
    const root: ParentNode = this.renderRoot;
    const rowEl = root.querySelector<HTMLElement>(
      `.data-table-row[data-index="${rowIndex}"]`
    );
    if (!rowEl) return;
    const cellEl = rowEl.querySelector<HTMLElement>(
      `[data-field="${CSS.escape(fieldName)}"]`
    );
    if (!cellEl) return;
    const focusable = cellEl.querySelector<HTMLElement>(
      'sl-input, sl-select, sl-checkbox, sl-textarea, sl-radio-group, sl-switch, sl-color-picker, input:not([type="hidden"]), select, textarea, button, [tabindex="0"]'
    );
    if (focusable) {
      focusable.focus();
      // For native inputs, also select contents so Tab-into-cell behaves
      // like a spreadsheet (overwrite-on-type).
      const native = focusable as HTMLInputElement;
      if (typeof native.select === 'function' && 'value' in native) {
        try {
          native.select();
        } catch {
          /* selection may throw on unsupported types */
        }
      }
    }
  }

  /**
   * Synthesize a new row via {@link addRowFactory}, dispatch `row-add`,
   * and queue focus on its first editable cell once the consumer pushes
   * it into the source array (which arrives back via the data prop).
   */
  private triggerAddRow(insertIndex: number) {
    if (!this.addRowFactory) return;
    const newItem = this.addRowFactory();
    const fields = this.getEditableFields(newItem);
    if (fields.length) {
      this._pendingFocus = {
        rowIndex: insertIndex,
        fieldName: fields[0].fieldName,
      };
    }
    const detail: DataTableRowAddDetail = {
      table: this,
      newItem,
      insertIndex,
    };
    this.dispatchCustom('row-add', detail);
  }

  // ────────────────────────────────────────────────────────────────────────

  private getCellWidthStyle(colDef: DataTableColumnDef) {
    const styles: Record<string, string> = {};

    // Use explicit width if provided
    if (colDef.width) {
      styles.flex = `0 0 ${colDef.width}`;
      styles.width = colDef.width;
    } else if (this.autoSizeColumns) {
      // Use computed width if auto-sizing is enabled and width was calculated
      const computedWidth = this._computedWidths.get(colDef.fieldName);
      if (computedWidth) {
        styles.flex = `0 0 ${computedWidth}px`;
        styles.width = `${computedWidth}px`;
      }
      // If no computed width yet, use auto to allow measurement
    }

    if (colDef.minWidth) {
      styles.minWidth = colDef.minWidth;
    }
    if (colDef.maxWidth) {
      styles.maxWidth = colDef.maxWidth;
    }
    return styleMap(styles);
  }

  private isVisible(column: DataTableColumnDef): boolean {
    if (column.visible !== undefined) {
      return column.visible;
    }

    // Default to true if visible is not set
    return true;
  }

  private getVisibleColumnGroups(): {
    ordered: Array<{ column: DataTableColumnDef; index: number }>;
    left: Array<{ column: DataTableColumnDef; index: number }>;
    center: Array<{ column: DataTableColumnDef; index: number }>;
    right: Array<{ column: DataTableColumnDef; index: number }>;
    total: number;
  } {
    const ordered: Array<{ column: DataTableColumnDef; index: number }> = [];
    const left: typeof ordered = [];
    const center: typeof ordered = [];
    const right: typeof ordered = [];
    let visibleIndex = 0;
    this.columns.forEach((column) => {
      if (!this.isVisible(column)) return;
      const entry = { column, index: visibleIndex };
      ordered.push(entry);
      if (column.fixed === 'left') left.push(entry);
      else if (column.fixed === 'right') right.push(entry);
      else center.push(entry);
      visibleIndex++;
    });
    return { ordered, left, center, right, total: visibleIndex };
  }

  public rowTemplate(item: object, index: number): TemplateResult {
    const groups = this.getVisibleColumnGroups();
    const columnCount = groups.total;

    const renderSegmentCells = (
      entries: Array<{ column: DataTableColumnDef; index: number }>
    ) =>
      entries.map(({ column, index: columnIndex }) =>
        this.columnTemplate(item, index, column, columnIndex, columnCount)
      );

    const leftCells = renderSegmentCells(groups.left);
    const centerCells = renderSegmentCells(groups.center);
    const rightCells = renderSegmentCells(groups.right);

    const itemRowClasses = this.getItemRowClass(item);
    const extensionPromise = this.createExtensionTemplate(item);
    const extensionTemplateWrapper = extensionPromise
      ? html`${until(
          extensionPromise.then(
            (result) => html`
              <div class="data-table-row-extension">${result.template}</div>
            `
          ),
          html`<div class="data-table-row-extension">
            <h1>Loading....</h1>
          </div>`
        )}`
      : undefined;

    const rowClick = (e: Event) => {
      e.stopImmediatePropagation();
      this.rowClicked(item);
    };
    const editingActive = this.editMode !== 'none';
    const rowKeyDown = (event: KeyboardEvent) => {
      // Edit-mode nav takes precedence when focus is inside an editor cell.
      if (editingActive && event.target !== event.currentTarget) {
        this.handleEditKeyDown(event, item, index);
        if (event.defaultPrevented) return;
      }
      this.handleRowKeyDown(event, item);
    };
    const rowFocusIn = () => {
      this._focusedRowIndex = index;
    };
    // check if current item key is in array, if so, add custom class to highlight row
    const itemKey = this.getPrimaryKey(item);
    const isSelected = this.selectedKeys.indexOf(itemKey) > -1;
    const enableDragDrop = this.enableDragOnRow(item);
    const noDrop: string =
      this.enableDrag === 'true' && !enableDragDrop ? this.noDropClass : '';

    const clickable = this.clickrows || this.isSelectable;
    const listenKeys = clickable || editingActive;
    const beforeRowTemplate = this.beforeRowTemplate(item, index);
    const afterRowTemplate = this.afterRowTemplate(item, index);

    return html`${beforeRowTemplate}
      <div
        draggable=${enableDragDrop ? 'true' : 'false'}
        @click=${clickable ? rowClick : undefined}
        @keydown=${listenKeys ? rowKeyDown : undefined}
        @focusin=${editingActive ? rowFocusIn : undefined}
        role=${ifDefined(clickable ? 'button' : undefined)}
        tabindex=${ifDefined(clickable ? 0 : undefined)}
        aria-selected=${ifDefined(
          this.isSelectable ? (isSelected ? 'true' : 'false') : undefined
        )}
        class="${this.rowClass} ${itemRowClasses} data-table-row ${
          isSelected ? 'selected' : ''
        } ${noDrop}"
        @dragstart="${this.handleDragStart}"
        @dragover="${this.handleDragOver}"
        @dragleave="${this.handleDragLeave}"
        @drop="${this.handleDrop}"
        @dragend="${this.handleDragEnd}"
        data-index="${index}"
        data-rowid=${this.getRowId(item) ?? nothing}
      >
        ${
          groups.left.length
            ? html`<div
                class="data-table-row-segment data-table-row-segment-left"
              >
                ${leftCells}
              </div>`
            : nothing
        }
        ${
          groups.center.length
            ? html`<div
                class="data-table-row-segment data-table-row-segment-center"
              >
                <div class="data-table-row-center-track">${centerCells}</div>
              </div>`
            : nothing
        }
        ${
          groups.right.length
            ? html`<div
                class="data-table-row-segment data-table-row-segment-right"
              >
                ${rightCells}
              </div>`
            : nothing
        }
      </div>
      ${extensionTemplateWrapper ?? ''}${afterRowTemplate}`;
  }

  private handleRowKeyDown(event: KeyboardEvent, item: object) {
    if (!this.clickrows && !this.isSelectable) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.rowClicked(item);
  }

  private handleTouchCancel(_e: TouchEvent) {
    this.cleanupDragEnd();
  }

  private dragtouchstart(event: TouchEvent) {
    const row = (event.target as HTMLElement).closest<HTMLDivElement>(
      `.${this.rowClass}`
    );

    if (!row?.draggable) return;

    row.classList.add('ghost-row');
    this.dragSrcEl = row;
    this.draggedIndex = Number(row.dataset.index);
  }

  private dragtouchmove(event: TouchEvent) {
    event.preventDefault();
    const touch = event.touches[0];

    const targetRow: HTMLDivElement | null | undefined = document
      .elementFromPoint(touch.clientX, touch.clientY)
      ?.closest(`.${this.rowClass}[draggable="true"]`);
    if (!targetRow) {
      this.lastDropTarget?.classList.remove('drop-target-at');
      this.lastDropTarget = null;
      return;
    }

    if (targetRow !== this.dragSrcEl && targetRow !== this.lastDropTarget) {
      this.lastDropTarget?.classList.remove('drop-target-at');

      targetRow.classList.add('drop-target-at');
      this.lastDropTarget = targetRow;
    }
  }

  private dragtouchend(_event: TouchEvent) {
    if (this.lastDropTarget) this.dropHandle(this.lastDropTarget);

    this.cleanupDragEnd();
  }

  private handleDragStart(e: DragEvent): void {
    const target = e.currentTarget as HTMLDivElement;

    if (this.enableDrag !== 'true') {
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    this.dragSrcEl = target;
    this.draggedIndex = Number(target.dataset.index);
    target.classList.add('ghost-row');
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setDragImage(target, 0, 0);
    }
  }

  private handleDragOver(e: DragEvent): void {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    const target = e.currentTarget as HTMLDivElement;

    if (this.enableDrag !== 'true') {
      e.stopPropagation();
      return;
    }

    if (target !== this.dragSrcEl) {
      target.classList.add('drop-target-at');
    }
  }

  private handleDragLeave(e: DragEvent): void {
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('drop-target-at');
  }

  private handleDrop(e: DragEvent) {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    this.dropHandle(target);
  }

  private dropHandle(target: HTMLElement) {
    if (this.enableDrag !== 'true') {
      return;
    }

    if (
      target !== this.dragSrcEl &&
      this.draggedIndex !== null &&
      !target.classList.contains(this.noDropClass)
    ) {
      const insertAt = Number(target.dataset.index);
      const insertObject: TableItemMove = {
        sourceIndex: this.draggedIndex,
        targetIndex: insertAt,
      };
      this.dispatchCustom('-table-item-move', insertObject);
    }
  }

  private handleDragEnd(e: DragEvent): void {
    e.preventDefault();
    this.cleanupDragEnd();
  }

  private cleanupDragEnd() {
    const rows = this.querySelectorAll(`.${this.rowClass}`);
    rows.forEach((row) => {
      row.classList.remove('ghost-row');
      row.classList.remove('drop-target-at');
    });
    // Ensure cleanup on drag end
    this.dragSrcEl = null;
    this.draggedIndex = null;
  }

  protected createExtensionTemplate(
    item: object
  ): Promise<DataTableExtensionResult> | null {
    const itemKey = this.getPrimaryKey(item);
    const extensionProvider = this._extensions.find(
      (x) => x.itemKey === itemKey
    )?.extensionProvider;
    if (!extensionProvider) return null;

    const normalizeResult = (
      result: TemplateResult | DataTableExtensionResult
    ): DataTableExtensionResult => {
      // Check if it's already a compound result with template property
      if (result && typeof result === 'object' && 'template' in result) {
        return result as DataTableExtensionResult;
      }
      // It's a plain TemplateResult, wrap it
      return { template: result as TemplateResult };
    };

    if (typeof extensionProvider === 'function') {
      const func = extensionProvider as DataTableRowExtensionTemplateEvent;
      const result = func(item);
      if (result instanceof Promise) {
        return result.then(normalizeResult);
      }
      return Promise.resolve(normalizeResult(result));
    } else {
      if (extensionProvider instanceof Promise) {
        return extensionProvider.then(normalizeResult);
      }
      return Promise.resolve(normalizeResult(extensionProvider));
    }
  }

  private setupScrollContainers() {
    const newBody = this.querySelector<HTMLElement>('.data-table-scroll');
    const newHeader = this.querySelector<HTMLElement>('.data-table-header');

    if (!newBody || !newHeader) {
      this.teardownScrollContainers();
      return;
    }

    if (this.bodyScrollElement !== newBody) {
      this.attachBodyScrollElement(newBody);
    }

    this.headerContainerElement = newHeader;
    this.syncHeaderScrollPosition();
  }

  private attachBodyScrollElement(element: HTMLElement) {
    this.detachBodyScrollElement();
    this.bodyScrollElement = element;
    this.bodyScrollListener = () => this.syncHeaderScrollPosition();
    element.addEventListener('scroll', this.bodyScrollListener, {
      passive: true,
    });
    if (this.resizeObserver) this.resizeObserver.observe(element);
  }

  private detachBodyScrollElement() {
    if (this.bodyScrollElement && this.bodyScrollListener) {
      this.bodyScrollElement.removeEventListener(
        'scroll',
        this.bodyScrollListener
      );
    }
    if (this.resizeObserver && this.bodyScrollElement) {
      this.resizeObserver.unobserve(this.bodyScrollElement);
    }
    this.bodyScrollElement = null;
    this.bodyScrollListener = undefined;
  }

  private teardownScrollContainers() {
    this.detachBodyScrollElement();
    if (this.headerContainerElement) {
      const headerRow = this.headerContainerElement.querySelector<HTMLElement>(
        '.data-table-header-row'
      );
      headerRow?.style.removeProperty('transform');
      headerRow?.style.removeProperty('width');
      this.headerContainerElement.style.removeProperty('padding-right');
    }
    this.headerContainerElement = null;
  }

  private syncHeaderScrollPosition() {
    // Header is inside scroll container, so horizontal scroll works natively.
    // We only need to handle the scrollbar gutter compensation for header
    // and sync the control scroller position.

    if (this.headerContainerElement && this.bodyScrollElement) {
      const gutter =
        this.bodyScrollElement.offsetWidth - this.bodyScrollElement.clientWidth;
      if (gutter > 0)
        this.headerContainerElement.style.paddingRight = `${gutter}px`;
      else this.headerContainerElement.style.removeProperty('padding-right');
    }
  }

  private observeSizeChanges() {
    if (typeof ResizeObserver === 'undefined') return;
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.queueFixedColumnOffsets();
        this.syncHeaderScrollPosition();
        this.updateScrollContainerWidth();
      });
    }
    this.resizeObserver.observe(this);
    if (this.bodyScrollElement)
      this.resizeObserver.observe(this.bodyScrollElement);
  }

  private updateScrollContainerWidth() {
    if (this.bodyScrollElement) {
      const width = this.bodyScrollElement.clientWidth;
      this.style.setProperty('--data-table-scroll-width', `${width}px`);
    }
  }

  /**
   * Measures header and body cells to compute optimal column widths.
   * Uses an offscreen measurement container to get accurate natural widths.
   * Only affects columns without explicit width property.
   */
  private computeAutoColumnWidths() {
    if (!this.autoSizeColumns) {
      return;
    }

    // Get columns that need auto-sizing (no explicit width)
    const columnsToMeasure = this.columns.filter(
      (col) => !col.width && this.isVisible(col)
    );
    if (columnsToMeasure.length === 0) return;

    // Use renderRoot (shadowRoot) to query elements in Shadow DOM
    const root = this.renderRoot;
    const headerRow = root.querySelector<HTMLElement>('.data-table-header-row');
    const contentArea = root.querySelector<HTMLElement>('.data-table-content');
    if (!headerRow) return;

    // Create an offscreen measurement container
    const measureContainer = document.createElement('div');
    measureContainer.style.cssText = `
      position: absolute;
      visibility: hidden;
      height: auto;
      width: auto;
      white-space: nowrap;
      font-family: var(--data-table-font, 'Space Grotesk', 'Segoe UI', system-ui, sans-serif);
      font-size: 0.875rem;
      padding: 0.35rem 0.75rem;
    `;
    document.body.appendChild(measureContainer);

    // Separate container for header (slightly different styling)
    const headerMeasureContainer = document.createElement('div');
    headerMeasureContainer.style.cssText = `
      position: absolute;
      visibility: hidden;
      height: auto;
      width: auto;
      white-space: nowrap;
      font-family: var(--data-table-font, 'Space Grotesk', 'Segoe UI', system-ui, sans-serif);
      font-size: 0.78rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.02em;
      padding: 0.35rem 0.75rem;
    `;
    document.body.appendChild(headerMeasureContainer);

    const newWidths = new Map<string, number>();
    const MIN_WIDTH = 60; // Minimum column width in pixels
    const EXTRA_PADDING = 16; // Extra padding for sort icons, borders, etc.

    for (const colDef of columnsToMeasure) {
      let maxWidth = MIN_WIDTH;
      const colIndex = this.columns.findIndex(
        (c) => c.fieldName === colDef.fieldName && this.isVisible(c)
      );
      console.log(
        '[auto-size] Processing column',
        colDef.fieldName,
        'at colIndex',
        colIndex
      );

      // Measure header cell content
      const headerCell = headerRow.querySelector<HTMLElement>(
        `.data-table-cell[data-col-index="${colIndex}"]`
      );
      console.log(
        '[auto-size] headerCell found:',
        !!headerCell,
        'selector:',
        `.data-table-cell[data-col-index="${colIndex}"]`
      );
      if (headerCell) {
        // Clone the content to measure
        headerMeasureContainer.innerHTML = headerCell.innerHTML;
        const headerWidth = headerMeasureContainer.offsetWidth + EXTRA_PADDING;
        maxWidth = Math.max(maxWidth, headerWidth);
        console.log(
          '[auto-size] Header width for',
          colDef.fieldName,
          ':',
          headerWidth
        );
      }

      // Measure body cells (sample first 20 rows for performance)
      if (contentArea) {
        const bodyCells = contentArea.querySelectorAll<HTMLElement>(
          `.data-table-cell[data-col-index="${colIndex}"]`
        );
        let rowCount = 0;
        for (const cell of bodyCells) {
          if (rowCount >= 20) break;
          measureContainer.innerHTML = cell.innerHTML;
          const cellWidth = measureContainer.offsetWidth + EXTRA_PADDING;
          maxWidth = Math.max(maxWidth, cellWidth);
          rowCount++;
        }
      }

      // Apply max-width constraint if specified
      if (colDef.maxWidth) {
        const maxWidthPx = parseInt(colDef.maxWidth, 10);
        if (!isNaN(maxWidthPx)) {
          maxWidth = Math.min(maxWidth, maxWidthPx);
        }
      }

      // Apply min-width constraint if specified
      if (colDef.minWidth) {
        const minWidthPx = parseInt(colDef.minWidth, 10);
        if (!isNaN(minWidthPx)) {
          maxWidth = Math.max(maxWidth, minWidthPx);
        }
      }

      newWidths.set(colDef.fieldName, Math.ceil(maxWidth));
    }

    // Clean up measurement containers
    document.body.removeChild(measureContainer);
    document.body.removeChild(headerMeasureContainer);

    // Only update if widths have changed
    let hasChanges = false;
    for (const [field, width] of newWidths) {
      if (this._computedWidths.get(field) !== width) {
        hasChanges = true;
        break;
      }
    }

    if (hasChanges) {
      this._computedWidths = newWidths;
      // Force re-render to apply the new widths
      this.requestUpdate();
    } else {
      //
    }
  }

  private scheduleAutoSize() {
    if (!this.autoSizeColumns || this._autoSizeScheduled) return;
    this._autoSizeScheduled = true;

    // Wait for render to complete, then measure
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._autoSizeScheduled = false;
        this.computeAutoColumnWidths();
      });
    });
  }

  private queueFixedColumnOffsets() {
    if (
      typeof window === 'undefined' ||
      typeof requestAnimationFrame === 'undefined'
    ) {
      this.applyFixedColumnOffsets();
      return;
    }
    if (this.fixedColumnOffsetFrame !== null) return;
    this.fixedColumnOffsetFrame = requestAnimationFrame(() => {
      this.fixedColumnOffsetFrame = null;
      this.applyFixedColumnOffsets();
    });
  }

  private applyFixedColumnOffsets() {
    const hasFixedColumns = this.columns.some(
      (column) => column.fixed && this.isVisible(column)
    );
    if (!hasFixedColumns) {
      this.clearFixedColumnOffsets();
      return;
    }

    const headerRow = this.querySelector<HTMLElement>('.data-table-header-row');
    if (!headerRow) return;

    const headerCells = Array.from(
      headerRow.querySelectorAll<HTMLElement>(
        '.data-table-cell[data-col-index]'
      )
    );
    if (!headerCells.length) return;

    const leftOffsets = new Map<number, number>();
    let cumulativeLeft = 0;
    headerCells
      .filter((cell) => cell.dataset.fixed === 'left')
      .sort(
        (a, b) =>
          Number(a.dataset.colIndex ?? 0) - Number(b.dataset.colIndex ?? 0)
      )
      .forEach((cell) => {
        const columnIndex = Number(cell.dataset.colIndex ?? 0);
        leftOffsets.set(columnIndex, cumulativeLeft);
        cumulativeLeft += cell.getBoundingClientRect().width;
      });

    const rightOffsets = new Map<number, number>();
    let cumulativeRight = 0;
    headerCells
      .filter((cell) => cell.dataset.fixed === 'right')
      .sort(
        (a, b) =>
          Number(b.dataset.colIndex ?? 0) - Number(a.dataset.colIndex ?? 0)
      )
      .forEach((cell) => {
        const columnIndex = Number(cell.dataset.colIndex ?? 0);
        rightOffsets.set(columnIndex, cumulativeRight);
        cumulativeRight += cell.getBoundingClientRect().width;
      });

    this.applyOffsetsForSide(leftOffsets, 'left');
    this.applyOffsetsForSide(rightOffsets, 'right');
  }

  private applyOffsetsForSide(
    offsets: Map<number, number>,
    side: 'left' | 'right'
  ) {
    const cells = this.querySelectorAll<HTMLElement>(
      `.data-table-cell[data-fixed="${side}"]`
    );
    cells.forEach((cell) => {
      const columnIndex = Number(cell.dataset.colIndex ?? '-1');
      if (!offsets.has(columnIndex)) {
        cell.style.removeProperty(side);
        return;
      }
      cell.style.setProperty(side, `${offsets.get(columnIndex)}px`);
    });
  }

  private clearFixedColumnOffsets() {
    const cells = this.querySelectorAll<HTMLElement>(
      '.data-table-cell[data-fixed]'
    );
    cells.forEach((cell) => {
      cell.style.removeProperty('left');
      cell.style.removeProperty('right');
    });
  }

  protected rowClicked(item: object) {
    if (this.isSelectable) {
      const itemKey = this.getPrimaryKey(item);

      // In row-edit mode, leaving the previously-selected row should
      // commit it before we move selection. We fire the row-commit
      // synchronously and ignore preventNav here — click-driven moves
      // can't pause for an async confirmation cleanly. If the consumer
      // needs to block, they should listen to the click and call
      // event.preventDefault() at the source.
      if (
        this.editMode === 'row' &&
        this.selectmode === 'single' &&
        this.selectedKeys.length
      ) {
        const prevKey = this.selectedKeys[0];
        if (prevKey !== itemKey) {
          const prevItem = this.data.find(
            (r) => this.getPrimaryKey(r as object) === prevKey
          );
          if (prevItem) {
            const prevIndex = this.data.indexOf(prevItem);
            void this.commitRowInternal(prevItem as object, prevIndex);
          }
        }
      }

      if (this.selectmode === 'single') {
        this.selectedKeys = [itemKey];
      } else if (this.selectmode === 'multi') {
        const keyIndex = this.selectedKeys.indexOf(itemKey);
        if (keyIndex === -1) {
          this.selectedKeys = [
            ...this.selectedKeys.filter((x) => x !== itemKey),
            itemKey,
          ];
        } else {
          this.selectedKeys = this.selectedKeys.filter((x) => x !== itemKey);
        }
      }
      this.rowSelectEvent();

      // Row mode: queue focus on the first editable cell of the newly
      // selected row so the user can type immediately.
      if (this.editMode === 'row' && this.selectedKeys.includes(itemKey)) {
        const fields = this.getEditableFields(item);
        if (fields.length) {
          const rowIndex = this.data.indexOf(item);
          if (rowIndex >= 0) {
            this._pendingFocus = {
              rowIndex,
              fieldName: fields[0].fieldName,
            };
          }
        }
      }
    }
    if (this.clickrows) {
      this.rowClickEvent(item);
    }
  }

  protected getPageData(): object[] {
    //can be converted to return a range for the page;
    return this.data;
  }

  protected calculateFirstIndex(
    _data: unknown[],
    pageIndex: number,
    pageLength: number
  ) {
    return pageIndex * pageLength;
  }

  protected getColumnHeaderTemplate(
    defs: DataTableColumnDef,
    index: number,
    totalColumns: number
  ) {
    const columnSortEvent = (e: Event) => {
      e.stopImmediatePropagation();
      if (this.sortField === defs.fieldName) {
        this.sortAscending = !this.sortAscending;
      } else {
        this.sortField = defs.fieldName;
        this.sortAscending = defs.isDefaultSortDirectionAscending ?? true;
      }
      this.pageIndex = 0;
    };

    const cellStyle = this.getCellWidthStyle(defs);
    const alignClass = defs.align ? `text-${defs.align}` : '';

    if (defs.sortable) {
      const isAscendingSort =
        this.isSortingAscending ?? defs.isDefaultSortDirectionAscending;
      const isColumnBeingSorted = this.sortField === defs.fieldName;
      const sortDirectionClass = isColumnBeingSorted
        ? isAscendingSort
          ? 'sorting_asc'
          : 'sorting_desc'
        : '';
      const posClass =
        index === 0 ? 'first' : index === totalColumns - 1 ? 'last' : '';
      const fixedClass = defs.fixed
        ? ` data-table-cell--fixed data-table-cell--fixed-${defs.fixed}`
        : '';
      const fixedAttr = defs.fixed ?? nothing;
      const classes =
        `${this.colClass} ${defs.classes ?? ''} sorting ${sortDirectionClass} ${posClass} ${fixedClass} ${alignClass}`.trim();
      return html` <div
        role="button"
        @click=${columnSortEvent}
        class="${classes}"
        style=${cellStyle}
        data-col-index=${index}
        data-fixed=${fixedAttr}
      >
        ${awaitableTemplate(defs.title)}
      </div>`;
    }
    const posClass =
      index === 0 ? 'first' : index === totalColumns - 1 ? 'last' : '';
    const fixedClass = defs.fixed
      ? ` data-table-cell--fixed data-table-cell--fixed-${defs.fixed}`
      : '';
    const fixedAttr = defs.fixed ?? nothing;
    return html` <div
      class="${this.colClass} ${
        defs.classes ?? ''
      } sorting-disabled ${posClass} ${fixedClass} ${alignClass}"
      style=${cellStyle}
      data-col-index=${index}
      data-fixed=${fixedAttr}
    >
      ${awaitableTemplate(defs.title)}
    </div>`;
  }

  protected headerTemplate(): unknown {
    const groups = this.getVisibleColumnGroups();
    const totalColumns = groups.total;
    const renderSegment = (
      entries: Array<{ column: DataTableColumnDef; index: number }>,
      segment: 'left' | 'center' | 'right'
    ) => {
      if (!entries.length) return nothing;
      const content = entries.map(({ column, index }) =>
        this.getColumnHeaderTemplate(column, index, totalColumns)
      );
      if (segment === 'center') {
        return html`<div
          class="data-table-header-segment data-table-header-segment-center"
        >
          <div class="data-table-header-center-track">${content}</div>
        </div>`;
      }
      const segmentClass =
        segment === 'left'
          ? 'data-table-header-segment-left'
          : 'data-table-header-segment-right';
      return html`<div class="data-table-header-segment ${segmentClass}">
        ${content}
      </div>`;
    };

    return html`
      ${renderSegment(groups.left, 'left')}
      ${renderSegment(groups.center, 'center')}
      ${renderSegment(groups.right, 'right')}
    `;
  }

  protected footerTemplate(): unknown {
    if (
      !this.columns.some(
        (x) => typeof x.columnAggregate === 'function' && this.isVisible(x)
      )
    )
      return html``;

    const getTotalColTemplate = (
      column: DataTableColumnDef,
      index: number,
      totalColumns: number
    ) => {
      const value =
        typeof column.columnAggregate === 'function'
          ? column.columnAggregate()
          : html``;
      const posClass =
        index === 0 ? 'first' : index === totalColumns - 1 ? 'last' : '';
      const fixedClass = column.fixed
        ? ` data-table-cell--fixed data-table-cell--fixed-${column.fixed}`
        : '';
      const fixedAttr = column.fixed ?? nothing;
      const cellStyle = this.getCellWidthStyle(column);

      return html` <div
        class="${this.colClass} ${
          column.classes ?? ''
        } ${posClass} ${fixedClass}"
        style=${cellStyle}
        data-col-index=${index}
        data-fixed=${fixedAttr}
      >
        ${value}
      </div>`;
    };

    const groups = this.getVisibleColumnGroups();
    const totalColumns = groups.total;
    const orderedColumns = [...groups.ordered].sort(
      (a, b) => a.index - b.index
    );
    const cols = orderedColumns.map(({ column, index }) =>
      getTotalColTemplate(column, index, totalColumns)
    );

    return html` <div class="${this.rowClass} data-table-row">${cols}</div>`;
  }

  private dispatchCustom(name: string, detail: object) {
    const newEvent = new CustomEvent(name, {
      detail: detail,
      bubbles: false,
      composed: true,
    });
    this.dispatchEvent(newEvent);
  }
}

const DATA_TABLE_TAG = 'app-data-table';

if (!customElements.get(DATA_TABLE_TAG)) {
  customElements.define(DATA_TABLE_TAG, DataTable);
}

declare global {
  interface HTMLElementTagNameMap {
    'app-data-table-paginator': DataTablePaginator;

    'app-data-table': DataTable;
  }
}
